import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Stack, TextField, Typography } from '@mui/material';
import { AddPhotoAlternateRounded, ChevronLeftRounded, ChevronRightRounded, DeleteOutlineRounded } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../../../firebase/firebase';
import type { SiteDraft } from '../../../sitePublishing/types';

export const MAX_GALLERY_PHOTOS = 30;
const ACCEPTED_IMAGES = 'image/jpeg,image/png,image/webp,image/gif';

type GalleryItem = SiteDraft['homepage']['gallery'][number];

const useAssetUrl = (path: string) => useQuery({
  queryKey: ['site-asset-url', path],
  queryFn: async () => (/^https:\/\//i.test(path) ? path : getDownloadURL(ref(storage, path))),
  enabled: Boolean(path),
  staleTime: 1000 * 60 * 30,
  retry: false,
});

const Thumbnail: React.FC<{
  item: GalleryItem;
  index: number;
  selected: boolean;
  uploading?: number;
  onSelect: () => void;
}> = ({ item, index, selected, uploading, onSelect }) => {
  const url = useAssetUrl(item.image);
  return (
    <Box component="button" type="button" onClick={onSelect} data-gallery-editor-index={index}
      aria-label={`Photo ${index + 1}${item.title ? `: ${item.title}` : ''}`} aria-pressed={selected}
      sx={{ position: 'relative', p: 0, border: 0, cursor: 'pointer', aspectRatio: '1', overflow: 'hidden',
        borderRadius: 2.5, bgcolor: 'grey.100', outline: '3px solid', outlineOffset: 2,
        outlineColor: selected ? 'primary.main' : 'transparent', transition: 'outline-color 120ms ease' }}>
      {url.data ? (
        <Box component="img" src={url.data} alt="" loading="lazy"
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : (
        <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}>
          <CircularProgress size={18} />
        </Box>
      )}
      <Box sx={{ position: 'absolute', left: 6, top: 6, minWidth: 22, height: 22, px: .75, borderRadius: 999,
        display: 'grid', placeItems: 'center', bgcolor: 'rgba(15,23,42,.72)', color: '#fff', fontSize: 12, fontWeight: 800 }}>
        {index + 1}
      </Box>
      {uploading !== undefined ? (
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.75)',
          fontWeight: 900 }}>{uploading}%</Box>
      ) : null}
    </Box>
  );
};

export type SiteBuilderGalleryPanelProps = {
  gallery: GalleryItem[];
  disabled: boolean;
  /** Index highlighted in the live preview, if any. */
  previewSelectedIndex?: number;
  uploadProgress: Record<string, number>;
  replacementInProgress: boolean;
  onUploadNew: (file: File) => void;
  onReplace: (index: number, file: File) => void;
  onUpdate: (index: number, field: 'title' | 'description', value: string) => void;
  onRemove: (index: number) => void;
  onMove: (from: number, to: number) => void;
};

/** Site Builder gallery editor: photo grid with per-photo caption, alt text, order, replace and remove. */
const SiteBuilderGalleryPanel: React.FC<SiteBuilderGalleryPanelProps> = ({
  gallery, disabled, previewSelectedIndex, uploadProgress, replacementInProgress,
  onUploadNew, onReplace, onUpdate, onRemove, onMove,
}) => {
  const [active, setActive] = useState<number | undefined>(previewSelectedIndex);
  useEffect(() => { if (previewSelectedIndex !== undefined) setActive(previewSelectedIndex); }, [previewSelectedIndex]);
  useEffect(() => { if (active !== undefined && active >= gallery.length) setActive(gallery.length ? gallery.length - 1 : undefined); },
    [active, gallery.length]);
  const activeItem = active === undefined ? undefined : gallery[active];
  const uploadingNew = uploadProgress['gallery-new'];
  const full = gallery.length >= MAX_GALLERY_PHOTOS;

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {gallery.length} of {MAX_GALLERY_PHOTOS} photos. Select a photo to add a caption, change its order or replace it.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
        {gallery.map((item, index) => (
          <Thumbnail key={`${item.image}-${index}`} item={item} index={index} selected={active === index}
            uploading={uploadProgress[`gallery-${index}`]} onSelect={() => setActive(active === index ? undefined : index)} />
        ))}
        <Box component="label" aria-label="Add photo" sx={{ aspectRatio: '1', borderRadius: 2.5, border: '2px dashed',
          borderColor: full || disabled ? 'divider' : 'rgba(25,118,210,.45)', bgcolor: 'rgba(25,118,210,.04)',
          display: 'grid', placeItems: 'center', textAlign: 'center', cursor: full || disabled ? 'not-allowed' : 'pointer',
          color: full || disabled ? 'text.disabled' : 'primary.main' }}>
          {uploadingNew !== undefined ? (
            <Typography fontWeight={900}>{uploadingNew}%</Typography>
          ) : (
            <Stack spacing={.25} alignItems="center">
              <AddPhotoAlternateRounded />
              <Typography variant="caption" fontWeight={800}>{full ? 'Gallery full' : 'Add photo'}</Typography>
            </Stack>
          )}
          <input hidden type="file" accept={ACCEPTED_IMAGES} disabled={disabled || full || uploadingNew !== undefined}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onUploadNew(file);
              event.target.value = '';
            }} />
        </Box>
      </Box>

      {activeItem && active !== undefined ? (
        <Box sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'primary.light', bgcolor: 'rgba(25,118,210,.03)' }}>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={.5}>
              <Typography fontWeight={850} sx={{ flex: 1 }}>Photo {active + 1}</Typography>
              <IconButton size="small" aria-label="Move earlier" disabled={disabled || active === 0}
                onClick={() => { onMove(active, active - 1); setActive(active - 1); }}><ChevronLeftRounded /></IconButton>
              <IconButton size="small" aria-label="Move later" disabled={disabled || active === gallery.length - 1}
                onClick={() => { onMove(active, active + 1); setActive(active + 1); }}><ChevronRightRounded /></IconButton>
            </Stack>
            <TextField size="small" label="Caption" value={activeItem.title ?? ''} disabled={disabled}
              inputProps={{ maxLength: 100 }} helperText="Shown over the photo on your site"
              onChange={(event) => onUpdate(active, 'title', event.target.value)} />
            <TextField size="small" label="Description for screen readers" value={activeItem.description ?? ''} disabled={disabled}
              inputProps={{ maxLength: 300 }} helperText="Describe what's in the photo"
              onChange={(event) => onUpdate(active, 'description', event.target.value)} />
            <Stack direction="row" spacing={1}>
              <Button component="label" variant="outlined" size="small"
                disabled={disabled || uploadProgress[`gallery-${active}`] !== undefined}>
                Replace
                <input hidden type="file" accept={ACCEPTED_IMAGES} onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) onReplace(active, file);
                  event.target.value = '';
                }} />
              </Button>
              <Button size="small" color="error" startIcon={<DeleteOutlineRounded />}
                disabled={disabled || replacementInProgress}
                onClick={() => { onRemove(active); setActive(undefined); }}>
                Remove
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
};

export default SiteBuilderGalleryPanel;
